/**
 * Benchmark distribution bar.
 *
 * Ported from `components/pipeline/benchmark/percentile-bar.tsx`. Renders the
 * min→max envelope as a red↔green gradient with the percentile markers plotted
 * along it. Gradient direction flips for lower-is-better metrics, matching the
 * reference app's "higher is better / lower is better" legend.
 */
type Props = {
  min: number;
  /** Null when the percentile_10 sibling row is missing; skip the marker. */
  p10: number | null;
  p25: number;
  p50: number;
  p75: number;
  p90: number;
  max: number;
  higherIsBetter: boolean;
  uom: string | null;
};

const MARKERS: {
  key: keyof Omit<Props, "higherIsBetter" | "uom">;
  label: string;
}[] = [
  { key: "min", label: "MIN" },
  { key: "p10", label: "P10" },
  { key: "p25", label: "P25" },
  { key: "p50", label: "P50" },
  { key: "p75", label: "P75" },
  { key: "p90", label: "P90" },
  { key: "max", label: "MAX" },
];

function fmt(v: number, uom: string | null) {
  if (uom === "USD") {
    return v >= 1000
      ? `$${Math.round(v).toLocaleString()}`
      : `$${v.toFixed(2)}`;
  }
  const n =
    Math.abs(v) >= 1000
      ? Math.round(v).toLocaleString()
      : v.toFixed(2).replace(/\.00$/, "");
  return uom && uom !== "Score" && uom !== "Ratio" ? `${n} ${uom}` : n;
}

export function PercentileBar(props: Props) {
  const { min, max, higherIsBetter, uom } = props;
  const span = max - min || 1;
  const pct = (v: number) =>
    Math.min(100, Math.max(0, ((v - min) / span) * 100));

  return (
    <div className="w-full">
      <div className="mb-1 flex items-center justify-between text-[10px] text-gray-400">
        <span>{fmt(min, uom)}</span>
        <span>{fmt(max, uom)}</span>
      </div>

      <div className="relative h-2.5 w-full overflow-hidden rounded-full">
        <div
          className="absolute inset-0"
          style={{
            background: higherIsBetter
              ? "linear-gradient(90deg,#dc2626 0%,#f59e0b 45%,#16a34a 100%)"
              : "linear-gradient(90deg,#16a34a 0%,#f59e0b 55%,#dc2626 100%)",
          }}
        />
        {MARKERS.slice(1, -1)
          .filter(m => props[m.key] != null)
          .map(m => (
            <div
              key={m.key}
              className="absolute top-0 h-full w-px bg-white/80"
              style={{ left: `${pct(props[m.key] as number)}%` }}
            />
          ))}
      </div>

      <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-500">
        {MARKERS.filter(m => props[m.key] != null).map(m => (
          <span key={m.key} className="tabular-nums">
            <span className="text-gray-400">{m.label}:</span>{" "}
            {fmt(props[m.key] as number, uom)}
          </span>
        ))}
      </div>
    </div>
  );
}

export default PercentileBar;
